import { useCallback, useEffect, useState } from "react";
import { demoApi } from "@/api/client";
import type { AnimalGroup, Herd, PieceType } from "@/api/types";
import { describeReadFailure } from "@/lib/apiFailure";

export type PieceTypesStatus = "loading" | "ready" | "error";

export interface UsePieceTypesResult {
  /** `null` until the first read resolves, and `null` if it failed — never `[]` for "unknown". */
  types: PieceType[] | null;
  status: PieceTypesStatus;
  error: string | null;
  /** Re-reads the list. Keeps the current rows on screen while it runs (no flash to "loading"). */
  refetch: () => void;
}

/**
 * ef-060-6 — the tenant's piece types from GET /api/demo/piece-types. `includeArchived` is part of
 * the request, so toggling "Show archived" is a fresh read, not a client-side filter over stale rows.
 */
export function usePieceTypes(includeArchived: boolean): UsePieceTypesResult {
  const [types, setTypes] = useState<PieceType[] | null>(null);
  const [status, setStatus] = useState<PieceTypesStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    demoApi
      .listPieceTypes(includeArchived, controller.signal)
      .then((rows) => {
        setTypes(rows);
        setError(null);
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setTypes(null);
        setError(describeReadFailure(err));
        setStatus("error");
      });
    return () => controller.abort();
  }, [includeArchived, tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { types, status, error, refetch };
}

export interface PieceLinkOptions {
  herds: Herd[];
  groups: AnimalGroup[];
  /** True once both reads have settled (a failed read just leaves its list empty). */
  loaded: boolean;
}

/**
 * The herds and groups a piece type may take its head count from — the existing livestock reads,
 * active herds only (an archived herd is not a place new work should be linked to). Failure is
 * non-fatal: the link is optional, so the dialog still saves an unlinked type.
 */
export function usePieceLinkOptions(): PieceLinkOptions {
  const [state, setState] = useState<PieceLinkOptions>({ herds: [], groups: [], loaded: false });

  useEffect(() => {
    const controller = new AbortController();
    void Promise.all([
      demoApi.getHerds(undefined, false, controller.signal).catch(() => [] as Herd[]),
      demoApi.getAnimalGroups(undefined, undefined, controller.signal).catch(() => [] as AnimalGroup[]),
    ]).then(([herds, groups]) => {
      if (!controller.signal.aborted) setState({ herds, groups, loaded: true });
    });
    return () => controller.abort();
  }, []);

  return state;
}
